import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { ExternalLink, Megaphone, Pin, Newspaper, Award, HandHeart, Sparkles, ChevronDown, ChevronUp, Twitter, Linkedin, Mail, Link2 } from "lucide-react";

const CAT_META = {
  event_update: { label: "Event Update", icon: Sparkles },
  press_release: { label: "Press Release", icon: Newspaper },
  award: { label: "Award", icon: Award },
  partnership: { label: "Partnership", icon: HandHeart },
};

export function SponsorsCarousel() {
  const [sponsors, setSponsors] = useState([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/sponsors").then((r) => setSponsors(r.data)).catch(() => {}); }, []);
  if (sponsors.length === 0) return null;

  return (
    <section data-testid="sponsors-carousel" className="relative py-20 md:py-24 border-t border-white/10 scroll-mt-nav">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <div>
            <p className="text-overline text-aawta-pink mb-3">FEATURED SPONSORS</p>
            <h2 className="font-display text-3xl md:text-5xl font-light text-white tracking-tight">
              The brands behind the <em className="italic text-aawta-pink">movement</em>.
            </h2>
          </div>
          <p className="text-white/50 text-sm max-w-md">
            {sponsors.length} sponsor{sponsors.length === 1 ? "" : "s"} powering the 3rd Annual Power to Drive Change.
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {sponsors.map((s) => (
            <a
              key={s.id}
              href={s.website_url || "#"}
              target={s.website_url ? "_blank" : undefined}
              rel="noopener noreferrer"
              data-testid={`sponsor-card-${s.id}`}
              className={`group border bg-aawta-charcoal/40 p-6 flex flex-col gap-3 transition-all hover:bg-aawta-charcoal/70 ${s.featured ? "border-aawta-pink/40" : "border-white/10 hover:border-white/30"}`}
            >
              <div className="h-16 flex items-center">
                {s.logo_url ? (
                  <img src={s.logo_url} alt={s.name} className="max-h-14 max-w-full object-contain opacity-90 group-hover:opacity-100 transition-opacity" />
                ) : (
                  <p className="font-display text-2xl text-white">{s.name}</p>
                )}
              </div>
              <div>
                {s.featured && <p className="text-overline text-aawta-pink mb-1">★ FEATURED</p>}
                {s.logo_url && <p className="font-display text-base text-white">{s.name}</p>}
                {s.tier && <p className="text-overline text-white/40 mt-0.5">{s.tier}</p>}
                {s.description && <p className="text-xs text-white/60 mt-2 line-clamp-2">{s.description}</p>}
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

export function PartnerSpotlights() {
  const [partners, setPartners] = useState([]);
  useEffect(() => { api.get("/partners").then((r) => setPartners(r.data)).catch(() => {}); }, []);
  if (partners.length === 0) return null;
  const strategic = partners.filter((p) => p.kind === "strategic");
  const media = partners.filter((p) => p.kind === "media");

  const renderGroup = (list, eyebrow, heading) => {
    if (list.length === 0) return null;
    return (
      <div className="mb-12 last:mb-0">
        <p className="text-overline text-aawta-pink mb-3">{eyebrow}</p>
        <h3 className="font-display text-2xl md:text-3xl text-white mb-6">{heading}</h3>
        <div className="grid md:grid-cols-2 gap-4">
          {list.map((p) => (
            <div key={p.id} data-testid={`partner-card-${p.id}`} className="border border-white/10 bg-aawta-charcoal/40 p-6 flex gap-5 items-start hover:border-aawta-pink/40 transition-all">
              {p.logo_url ? (
                <img src={p.logo_url} alt={p.name} className="w-20 h-20 object-contain bg-white/5 p-2 shrink-0" />
              ) : (
                <div className="w-20 h-20 bg-aawta-pink/10 flex items-center justify-center font-display text-2xl text-aawta-pink shrink-0">
                  {p.name.charAt(0)}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-display text-xl text-white">{p.name}</p>
                {p.description && <p className="text-sm text-white/60 mt-1">{p.description}</p>}
                {p.spotlight_quote && (
                  <p className="text-sm text-white/85 mt-3 italic border-l-2 border-aawta-pink pl-3">&ldquo;{p.spotlight_quote}&rdquo;</p>
                )}
                {p.website_url && (
                  <a href={p.website_url} target="_blank" rel="noopener noreferrer" className="text-overline text-aawta-pink mt-3 inline-flex items-center gap-1.5 hover:underline">
                    VISIT <ExternalLink size={11} />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <section data-testid="partner-spotlights" className="relative py-20 md:py-24 bg-aawta-charcoal/30 border-t border-white/10 scroll-mt-nav">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        {renderGroup(strategic, "STRATEGIC PARTNERS", "Building the future, together.")}
        {renderGroup(media, "MEDIA PARTNERS", "Telling the story.")}
      </div>
    </section>
  );
}

export function AnnouncementsFeed() {
  const [news, setNews] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/news?limit=6").then((r) => setNews(r.data)).catch(() => {}); }, []);
  if (news.length === 0) return null;

  const share = (n, channel) => {
    const url = `${window.location.origin}/#news-${n.id}`;
    const text = encodeURIComponent(`${n.title} — AAWTA Power to Drive Change Gala`);
    const links = {
      twitter: `https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      email: `mailto:?subject=${text}&body=${encodeURIComponent(url)}`,
    };
    if (channel === "copy") {
      navigator.clipboard?.writeText(url);
      return;
    }
    window.open(links[channel], "_blank", "noopener,noreferrer");
  };

  return (
    <section id="news" data-testid="announcements-feed" className="relative py-20 md:py-24 border-t border-white/10 scroll-mt-nav">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-3">
          <div>
            <p className="text-overline text-aawta-pink mb-3 flex items-center gap-2"><Megaphone size={12} /> NEWS & ANNOUNCEMENTS</p>
            <h2 className="font-display text-3xl md:text-5xl font-light text-white tracking-tight">
              Latest from <em className="italic text-aawta-pink">AAWTA</em>.
            </h2>
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {news.map((n) => {
            const meta = CAT_META[n.category] || CAT_META.event_update;
            const Icon = meta.icon;
            const expanded = expandedId === n.id;
            return (
              <article
                key={n.id} id={`news-${n.id}`}
                data-testid={`news-card-${n.id}`}
                className={`border border-white/10 bg-aawta-charcoal/40 transition-all overflow-hidden flex flex-col ${expanded ? "lg:col-span-3 md:col-span-2 border-aawta-pink/40" : "hover:border-aawta-pink/40"}`}
              >
                {n.image_url && <img src={n.image_url} alt={n.title} className={`w-full object-cover ${expanded ? "h-80" : "h-44"}`} />}
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <Icon size={12} className="text-aawta-pink" />
                    <span className="text-overline text-aawta-pink">{meta.label}</span>
                    {n.created_at && <span className="text-xs text-white/40 ml-auto">{new Date(n.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}</span>}
                    {n.pinned && <Pin size={11} className="text-yellow-300" />}
                  </div>
                  <h3 className="font-display text-xl text-white leading-tight">{n.title}</h3>
                  <p className={`text-sm text-white/70 mt-2 whitespace-pre-line ${expanded ? "" : "line-clamp-3"}`}>{n.body}</p>
                  <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
                    <button
                      data-testid={`news-read-more-${n.id}`}
                      onClick={() => setExpandedId(expanded ? null : n.id)}
                      className="text-overline text-aawta-pink hover:text-white inline-flex items-center gap-1 transition-colors"
                    >
                      {expanded ? <>SHOW LESS <ChevronUp size={12} /></> : <>READ MORE <ChevronDown size={12} /></>}
                    </button>
                    {expanded && (
                      <div className="flex items-center gap-2" data-testid={`news-share-${n.id}`}>
                        <span className="text-overline text-white/40">SHARE</span>
                        <ShareBtn icon={Twitter} onClick={() => share(n, "twitter")} label="Twitter" />
                        <ShareBtn icon={Linkedin} onClick={() => share(n, "linkedin")} label="LinkedIn" />
                        <ShareBtn icon={Mail} onClick={() => share(n, "email")} label="Email" />
                        <ShareBtn icon={Link2} onClick={() => share(n, "copy")} label="Copy link" />
                        {n.link_url && (
                          <a href={n.link_url} target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-aawta-pink" title="Open original">
                            <ExternalLink size={14} />
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ShareBtn({ icon: Icon, onClick, label }) {
  return (
    <button onClick={onClick} title={label} aria-label={label} className="p-1.5 text-white/50 hover:text-aawta-pink transition-colors">
      <Icon size={14} />
    </button>
  );
}
